"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { Globe } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from "@/ui/components/ui/dropdown-menu";
import { type Locale } from "@/config/locale";
import { setGeoPrefCookie } from "@/lib/geo/geo-pref-client";
import { useLocaleChannelState } from "./use-locale-channel-state";
import { useLocaleSwitchPath } from "./use-locale-switch-path";
import { LanguageDetailPanel } from "./language-detail-panel";

const triggerClass =
	"inline-flex h-9 w-9 items-center justify-center rounded-lg border border-transparent text-foreground transition-colors hover:border-primary/30 hover:bg-primary/5 hover:text-primary lg:h-10 lg:w-10";

export function LocaleChannelTrigger() {
	const t = useTranslations("localeChannel");
	const [open, setOpen] = useState(false);
	const { currentLocale, currentChannel, enabledLocales } = useLocaleChannelState();
	const router = useRouter();
	const localePath = useLocaleSwitchPath(currentLocale);

	const close = () => setOpen(false);

	const switchLocale = (locale: Locale) => {
		setGeoPrefCookie({ locale, channel: currentChannel });
		const next = localePath(locale);
		router.push(next);
		close();
	};

	return (
		<DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
			<DropdownMenuTrigger asChild>
				<button type="button" className={triggerClass} aria-label={t("triggerAria")}>
					<Globe className="h-5 w-5" />
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align="end"
				side="bottom"
				sideOffset={8}
				collisionPadding={16}
				className="flex max-h-[min(85vh,40rem)] w-[min(22rem,calc(100vw-2rem))] flex-col overflow-hidden p-0"
			>
				<div className="flex min-h-0 flex-1 flex-col">
					<LanguageDetailPanel
						currentLocale={currentLocale}
						enabledLocales={enabledLocales}
						onBack={close}
						onSelect={switchLocale}
					/>
				</div>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
